// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useState } from 'react';

import type { SurveyPart, SurveyType } from '../../../api/types';
import { activePart, capWithFocus, PART_GROUP_MIN, partsByRoom, ROOM_PART_LIMIT } from '../../../bygning/parts';
import { partChipClick, type Selection } from '../../../bygning/selection';
import { roomName } from '../../../kortlaegning/model';
import { TextLink } from '../../TextLink';
import { EvidenceButtons } from './EvidenceButtons';
import styles from './Inspector.module.css';

export interface PartChipsProps {
  type: SurveyType;
  sel: Selection;
  setSel: (patch: Partial<Selection>) => void;
  canEdit: boolean;
  /** "Tilføj del uden geometri" under this type. */
  onAddPart: () => void;
  /**
   * `ressourcer` (default): a click opens the part, Shift/⌘/Ctrl-click picks
   * it. `review` (Gennemgang): a click picks and focuses, with the evidence
   * buttons for the active part under the chips.
   */
  variant?: 'ressourcer' | 'review';
  /**
   * Ressourcer: the part last opened from here. Back on the type, its room
   * opens and its chip keeps the focus ring, so the list picks up where it was.
   */
  recent?: string | null;
}

/** Local UI state that belongs to one type's room (resets when the type changes). */
interface RoomRef {
  typeId: number;
  key: string;
}

/**
 * "Dele · N" (v8 `deleSec`): the type's parts as chips, Vælg alle / Ryd /
 * + Tilføj del. In Ressourcer a chip opens its part (`PartInspector`, the
 * right tile drills down) and Shift/⌘/Ctrl-click picks it for the PartBar;
 * in Gennemgang a chip picks and focuses the part, and the evidence buttons
 * for the active part sit under the chips — they only set the selection
 * (`?part=&ev=`), the evidence tile is the centre's.
 *
 * Above `PART_GROUP_MIN` parts the chips are rooms instead (`partsByRoom`,
 * "Stue · 24 · 3 valgt"): one room opens at a time to show its part chips —
 * the first `ROOM_PART_LIMIT`, then "Vis alle" — with "Vælg alle i {rum}".
 * The focused part (Gennemgang) or the one just closed (Ressourcer, `recent`)
 * opens its room and keeps its chip in view; "Åbn i tabel" hands the long
 * list to Tabel › Dele.
 */
export function PartChips({ type, sel, setSel, canEdit, onAddPart, variant = 'ressourcer', recent = null }: PartChipsProps) {
  const review = variant === 'review';
  const parts = [...type.parts].sort((a, b) => a.code.localeCompare(b.code));
  const picked = new Set(sel.selParts);
  const anyPicked = sel.selParts.length > 0;
  const active = review ? activePart(type, sel.partCode, sel.selParts) : null;
  const activeObj = parts.find((p) => p.code === active) ?? null;

  const grouped = parts.length > PART_GROUP_MIN;
  const groups = grouped ? partsByRoom({ parts }) : [];
  const panelId = useId();
  const [open, setOpen] = useState<RoomRef | null>(null);
  const [showAll, setShowAll] = useState<RoomRef | null>(null);
  const openKey = open?.typeId === type.id ? open.key : null;
  const openGroup = groups.find((g) => g.key === openKey) ?? null;

  // The focused part (or, back from a part's inspector, the one just closed) opens its room (one room at a time).
  const focusCode = sel.partCode ?? recent;
  const focusKey = groups.find((g) => g.parts.some((p) => p.code === focusCode))?.key ?? null;
  const typeId = type.id;
  useEffect(() => {
    if (focusKey !== null) setOpen({ typeId, key: focusKey });
  }, [focusCode, focusKey, typeId]);

  const pickAll = (codes: readonly string[]) => setSel({ selParts: [...new Set([...sel.selParts, ...codes])] });

  const chip = (p: SurveyPart, withRoom: boolean) => {
    const room = roomName(p);
    const cls = [styles.chip, focusCode === p.code ? styles.chipFocus : ''].filter(Boolean).join(' ');
    return (
      <button
        key={p.code}
        type="button"
        className={cls}
        aria-pressed={picked.has(p.code)}
        title={[p.code, room, p.note].filter(Boolean).join(' · ')}
        onClick={(e) => setSel(partChipClick(sel, p.code, e.shiftKey || e.metaKey || e.ctrlKey, variant))}
      >
        {p.code}
        {withRoom && room && <span className={styles.chipMuted}>{room}</span>}
      </button>
    );
  };

  const roomPanel = () => {
    if (!openGroup) return null;
    const all = showAll?.typeId === type.id && showAll.key === openGroup.key;
    const { shown, hidden } = all
      ? { shown: openGroup.parts, hidden: [] as SurveyPart[] }
      : capWithFocus(openGroup.parts, ROOM_PART_LIMIT, (p) => p.code === focusCode);
    return (
      <div id={panelId} className={styles.roomParts}>
        <TextLink size="sm" nowrap onClick={() => pickAll(openGroup.parts.map((p) => p.code))}>
          Vælg alle i {openGroup.name}
        </TextLink>
        <div className={styles.chips} role="group" aria-label={`Dele i ${openGroup.name}`}>
          {shown.map((p) => chip(p, false))}
          {hidden.length > 0 && (
            <TextLink className={styles.chipsMore} nowrap onClick={() => setShowAll({ typeId: type.id, key: openGroup.key })}>
              Vis alle {openGroup.parts.length}
            </TextLink>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className={styles.section} aria-label="Dele">
      <h3 className={styles.sectionHead}>
        <span>Dele · {parts.length}</span>
        <span className={styles.sectionTools}>
          {grouped && (
            <TextLink
              size="sm"
              nowrap
              title={`Vis delene i Tabel › Dele (søgning: ${type.name})`}
              onClick={() => setSel({ view: 'tabel', tview: 'dele', search: type.name })}
            >
              Åbn i tabel
            </TextLink>
          )}
          {parts.length > 0 && (
            <TextLink size="sm" nowrap onClick={() => pickAll(parts.map((p) => p.code))}>
              Vælg alle
            </TextLink>
          )}
          {anyPicked && (
            <TextLink size="sm" nowrap onClick={() => setSel({ selParts: [] })}>
              Ryd
            </TextLink>
          )}
          {canEdit && (
            <TextLink size="sm" nowrap onClick={onAddPart}>
              + Tilføj del
            </TextLink>
          )}
        </span>
      </h3>
      {parts.length === 0 ? (
        <p className={styles.sub}>Ingen dele endnu. Tilføj en del uden geometri, eller flyt dele hertil fra modellen.</p>
      ) : grouped ? (
        <>
          <div className={styles.chips} role="group" aria-label={`Rum med dele af ${type.name}`}>
            {groups.map((g) => {
              const isOpen = g.key === openKey;
              const n = g.parts.filter((p) => picked.has(p.code)).length;
              const cls = [styles.chip, isOpen ? styles.chipFocus : ''].filter(Boolean).join(' ');
              return (
                <button
                  key={g.key}
                  type="button"
                  className={cls}
                  aria-expanded={isOpen}
                  aria-controls={isOpen ? panelId : undefined}
                  title={isOpen ? `Skjul delene i ${g.name}` : `Vis delene i ${g.name}`}
                  onClick={() => setOpen(isOpen ? null : { typeId: type.id, key: g.key })}
                >
                  {g.name}
                  <span className={styles.chipMuted}>· {g.parts.length}</span>
                  {n > 0 && <span className={styles.chipMuted}>· {n} valgt</span>}
                </button>
              );
            })}
          </div>
          {roomPanel()}
        </>
      ) : (
        <div className={styles.chips} role="group" aria-label={`Dele i ${type.name}`}>
          {parts.map((p) => chip(p, true))}
        </div>
      )}
      {parts.length > 0 && (
        <p className={styles.sub}>
          {review
            ? 'Klik dele her eller i modellen. Shift-klik i modellen vælger på tværs af typer.'
            : 'Klik en del for at åbne den. Shift-klik vælger flere (også i modellen og på tværs af typer).'}
        </p>
      )}
      {activeObj && (
        <>
          <p className={styles.sub}>
            Evidens for del <b>{activeObj.code}</b>
            {roomName(activeObj) ? ` · ${roomName(activeObj)}` : ''}
          </p>
          <EvidenceButtons code={activeObj.code} sel={sel} setSel={setSel} />
        </>
      )}
    </section>
  );
}
